// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Badge, Box, Group, NumberInput, Progress, Select, Stack, Text, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { createReference, normalizeErrorString } from '@medplum/core';
import type { Practitioner, Reference, Task } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import {
  getCalendarMonthBounds,
  RPM_MONITORING_STATUSES,
  RPM_OBSERVATION_CODES,
  type RpmMonitoringStatus,
  type TriageSortState,
} from '@polarissleep/shared';
import { IconCircleCheck, IconCircleOff } from '@tabler/icons-react';
import type { DataTableSortStatus } from 'mantine-datatable';
import { DataTable } from 'mantine-datatable';
import { type JSX, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { TableAnchor } from '../../components/layout/TableAnchor';
import { type RemoteMonitoringEntry, useRemoteMonitoringQueue } from '../../hooks/useRemoteMonitoringQueue';
import { upsertRpmObservation } from '../../utils/rpmFhir';
import { getTotalMinutesColor } from '../../utils/rpmNotes';
import { withTenantScope } from '../../utils/tenant';
import { CreateTriageTaskModal, type TaskDetails } from './CreateTriageTaskModal';
import { RpmAddNoteModal } from './RpmAddNoteModal';
import { RpmContactInfoModal } from './RpmContactInfoModal';
import { RpmMonthNotesModal } from './RpmMonthNotesModal';
import { sortStatusToTriageSortState, triageSortStateToSortStatus } from './useTriageQueueUrlState';

interface RemoteMonitoringTableProps {
  page: number;
  pageSize: number;
  search: string;
  onPageChange: (page: number) => void;
  sortState: TriageSortState | null;
  onSortChange: (next: TriageSortState | null) => void;
  onTotalChange?: (total: number) => void;
  hideTestPatients: boolean;
}

const RPM_TASK_IDENTIFIER_SYSTEM = 'https://polarissleep.com/fhir/task/rpm';
const OPEN_TASK_STATUS_SEARCH = 'draft,requested,ready,received,accepted,in-progress';
const AIRVIEW_URL = 'https://airview.resmed.com/';

function toDateInputValue(iso: string | undefined): string {
  if (!iso) {
    return '';
  }
  const date = new Date(iso);
  if (!Number.isFinite(date.getTime())) {
    return '';
  }
  return date.toISOString().slice(0, 10);
}

function dateInputToIso(value: string): string | undefined {
  if (!value) {
    return undefined;
  }
  const date = new Date(`${value}T12:00:00`);
  return Number.isFinite(date.getTime()) ? date.toISOString() : undefined;
}

function requirementBadge(complete: boolean): JSX.Element {
  return (
    <Badge color={complete ? 'green' : 'gray'} variant="light" radius="xl">
      {complete ? 'Complete' : 'Not complete'}
    </Badge>
  );
}

function statusLabel(value: RpmMonitoringStatus | undefined): string {
  return RPM_MONITORING_STATUSES.find((status) => status.value === value)?.label ?? 'Select status';
}

function buildRpmTaskIdentifier(entry: RemoteMonitoringEntry): string {
  return `${entry.patientId}|rpm-follow-up`;
}

function isMatchingRpmTask(task: Task): boolean {
  return task.code?.text === 'RPM follow-up';
}

export function RemoteMonitoringTable({
  page,
  pageSize,
  search,
  onPageChange,
  sortState,
  onSortChange,
  onTotalChange,
  hideTestPatients,
}: RemoteMonitoringTableProps): JSX.Element {
  const navigate = useNavigate();
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const month = getCalendarMonthBounds(new Date()).month;
  const [adherenceDrafts, setAdherenceDrafts] = useState<Record<string, number | ''>>({});
  const [lastContactDrafts, setLastContactDrafts] = useState<Record<string, string>>({});
  const [savingKeys, setSavingKeys] = useState<Set<string>>(new Set());
  const [contactEntry, setContactEntry] = useState<RemoteMonitoringEntry | undefined>();
  const [noteEntry, setNoteEntry] = useState<RemoteMonitoringEntry | undefined>();
  const [notesEntry, setNotesEntry] = useState<RemoteMonitoringEntry | undefined>();
  const [taskEntry, setTaskEntry] = useState<RemoteMonitoringEntry | undefined>();
  const { entries, loading, total, refetch } = useRemoteMonitoringQueue({
    page,
    pageSize,
    search,
    sort: sortState ?? undefined,
    hideTestPatients,
  });

  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);

  useEffect(() => {
    onTotalChange?.(total);
  }, [onTotalChange, total]);

  const statusOptions = useMemo(() => [...RPM_MONITORING_STATUSES], []);

  const setSaving = (key: string, saving: boolean): void => {
    setSavingKeys((current) => {
      const next = new Set(current);
      if (saving) {
        next.add(key);
      } else {
        next.delete(key);
      }
      return next;
    });
  };

  const handleSaveAdherence = async (entry: RemoteMonitoringEntry): Promise<void> => {
    if (!entry.patientId) {
      return;
    }
    const value = adherenceDrafts[entry.patientId] ?? entry.adherenceNights;
    if (value === undefined || value === '') {
      return;
    }
    if (value < 0 || value > 30) {
      notifications.show({ color: 'red', message: 'Adherence must be between 0 and 30 nights.' });
      return;
    }
    const key = `${entry.patientId}|adherence`;
    setSaving(key, true);
    try {
      await upsertRpmObservation(medplum, {
        patientId: entry.patientId,
        month,
        code: RPM_OBSERVATION_CODES.adherenceNights30Day,
        existing: entry.rpmObservations[RPM_OBSERVATION_CODES.adherenceNights30Day],
        value: { integer: value },
      });
      notifications.show({ color: 'green', icon: <IconCircleCheck size={16} />, message: 'Adherence updated' });
      refetch();
    } catch (error) {
      notifications.show({ color: 'red', icon: <IconCircleOff size={16} />, message: normalizeErrorString(error) });
    } finally {
      setSaving(key, false);
    }
  };

  const handleSaveLastContact = async (entry: RemoteMonitoringEntry): Promise<void> => {
    if (!entry.patientId) {
      return;
    }
    const value = lastContactDrafts[entry.patientId] ?? toDateInputValue(entry.lastContactDate);
    const iso = dateInputToIso(value);
    if (!iso) {
      return;
    }
    const key = `${entry.patientId}|last-contact`;
    setSaving(key, true);
    try {
      await upsertRpmObservation(medplum, {
        patientId: entry.patientId,
        month,
        code: RPM_OBSERVATION_CODES.lastContactDate,
        existing: entry.rpmObservations[RPM_OBSERVATION_CODES.lastContactDate],
        effectiveDateTime: iso,
        value: { dateTime: iso },
      });
      notifications.show({ color: 'green', icon: <IconCircleCheck size={16} />, message: 'Last contact updated' });
      refetch();
    } catch (error) {
      notifications.show({ color: 'red', icon: <IconCircleOff size={16} />, message: normalizeErrorString(error) });
    } finally {
      setSaving(key, false);
    }
  };

  const handleSaveStatus = async (entry: RemoteMonitoringEntry, status: RpmMonitoringStatus): Promise<void> => {
    if (!entry.patientId) {
      return;
    }
    const key = `${entry.patientId}|status`;
    setSaving(key, true);
    try {
      await upsertRpmObservation(medplum, {
        patientId: entry.patientId,
        month,
        code: RPM_OBSERVATION_CODES.monitoringStatus,
        existing: entry.rpmObservations[RPM_OBSERVATION_CODES.monitoringStatus],
        value: { status },
      });
      notifications.show({ color: 'green', icon: <IconCircleCheck size={16} />, message: 'RPM status updated' });
      refetch();
    } catch (error) {
      notifications.show({ color: 'red', icon: <IconCircleOff size={16} />, message: normalizeErrorString(error) });
    } finally {
      setSaving(key, false);
    }
  };

  const handleCreateTask = async (
    entry: RemoteMonitoringEntry,
    owner: Reference<Practitioner>,
    details: TaskDetails
  ): Promise<void> => {
    if (!entry.patientId) {
      return;
    }
    const key = `${entry.patientId}|task`;
    setSaving(key, true);
    try {
      const taskIdentifier = buildRpmTaskIdentifier(entry);
      const [existingIdentifiedTasks, existingPatientTasks] = await Promise.all([
        medplum.searchResources('Task', {
          identifier: `${RPM_TASK_IDENTIFIER_SYSTEM}|${taskIdentifier}`,
          status: OPEN_TASK_STATUS_SEARCH,
          _count: '1',
        }),
        medplum.searchResources('Task', {
          patient: `Patient/${entry.patientId}`,
          status: OPEN_TASK_STATUS_SEARCH,
          _count: '50',
        }),
      ]);
      if (existingIdentifiedTasks.length > 0 || existingPatientTasks.some(isMatchingRpmTask)) {
        notifications.show({
          color: 'blue',
          icon: <IconCircleCheck size={16} />,
          message: 'An open RPM task already exists for this patient.',
        });
        setTaskEntry(undefined);
        return;
      }

      const task: Task = {
        resourceType: 'Task',
        identifier: [{ system: RPM_TASK_IDENTIFIER_SYSTEM, value: taskIdentifier }],
        status: 'requested',
        intent: 'order',
        priority: details.priority,
        restriction: details.dueDate ? { period: { end: `${details.dueDate}T23:59:59.000Z` } } : undefined,
        authoredOn: new Date().toISOString(),
        requester: profile ? createReference(profile) : undefined,
        owner,
        for: { reference: `Patient/${entry.patientId}`, display: entry.patientName },
        code: { text: 'RPM follow-up' },
        description: details.description,
      };
      await medplum.createResource(withTenantScope(task));
      notifications.show({ color: 'green', icon: <IconCircleCheck size={16} />, message: 'RPM task created' });
      setTaskEntry(undefined);
    } catch (error) {
      notifications.show({ color: 'red', icon: <IconCircleOff size={16} />, message: normalizeErrorString(error) });
    } finally {
      setSaving(key, false);
    }
  };

  const columns = [
    {
      accessor: 'patientName' as const,
      title: 'Patient',
      sortable: true,
      render: (entry: RemoteMonitoringEntry) => (
        <TableAnchor size="sm" onClick={() => navigate(`/Patient/${entry.patientId}`)} style={{ cursor: 'pointer' }}>
          {entry.patientName}
        </TableAnchor>
      ),
    },
    {
      accessor: 'adherenceNights' as const,
      title: 'Adherence (30-day)',
      sortable: false,
      render: (entry: RemoteMonitoringEntry) => {
        const patientId = entry.patientId ?? entry.patientName;
        const draft = entry.patientId ? (adherenceDrafts[entry.patientId] ?? entry.adherenceNights ?? '') : '';
        const value = typeof draft === 'number' ? draft : 0;
        return (
          <Group gap="sm" wrap="nowrap">
            <Box w={70}>
              <Progress value={(value / 30) * 100} color={entry.adherenceColor ?? 'gray'} size="sm" />
            </Box>
            <NumberInput
              aria-label={`Adherence nights for ${entry.patientName}`}
              min={0}
              max={30}
              size="xs"
              w={60}
              value={draft}
              disabled={!entry.patientId || savingKeys.has(`${patientId}|adherence`)}
              onChange={(next) => {
                if (entry.patientId) {
                  setAdherenceDrafts((current) => ({
                    ...current,
                    [entry.patientId as string]: typeof next === 'number' ? next : '',
                  }));
                }
              }}
              onBlur={() => handleSaveAdherence(entry).catch(console.error)}
            />
            <Text size="sm" c="dimmed">
              /30
            </Text>
          </Group>
        );
      },
    },
    {
      accessor: 'lastContactDate' as const,
      title: 'Last contact',
      sortable: false,
      render: (entry: RemoteMonitoringEntry) => {
        const patientId = entry.patientId ?? entry.patientName;
        const value = entry.patientId
          ? (lastContactDrafts[entry.patientId] ?? toDateInputValue(entry.lastContactDate))
          : '';
        return (
          <TextInput
            aria-label={`Last contact for ${entry.patientName}`}
            type="date"
            size="xs"
            w={150}
            value={value}
            disabled={!entry.patientId || savingKeys.has(`${patientId}|last-contact`)}
            onChange={(event) => {
              if (entry.patientId) {
                setLastContactDrafts((current) => ({
                  ...current,
                  [entry.patientId as string]: event.currentTarget.value,
                }));
              }
            }}
            onBlur={() => handleSaveLastContact(entry).catch(console.error)}
          />
        );
      },
    },
    {
      accessor: 'totalMinutes' as const,
      title: 'Total minutes',
      sortable: false,
      render: (entry: RemoteMonitoringEntry) => {
        const totalMinutes = entry.asyncMinutes + entry.syncMinutes;
        return (
          <Badge color={getTotalMinutesColor(totalMinutes)} variant="light" radius="xl">
            {totalMinutes} min
          </Badge>
        );
      },
    },
    {
      accessor: 'syncMinutes' as const,
      title: 'Sync Req',
      sortable: false,
      render: (entry: RemoteMonitoringEntry) => (
        <Stack gap={2}>
          {requirementBadge(entry.syncComplete)}
          <Text size="xs" c="dimmed">
            {entry.syncMinutes} min
          </Text>
        </Stack>
      ),
    },
    {
      accessor: 'monitoringStatus' as const,
      title: 'Status',
      sortable: false,
      render: (entry: RemoteMonitoringEntry) => {
        const patientId = entry.patientId ?? entry.patientName;
        return (
          <Select
            aria-label={`RPM status for ${entry.patientName}`}
            data={statusOptions}
            value={entry.monitoringStatus ?? null}
            placeholder={statusLabel(entry.monitoringStatus)}
            size="xs"
            w={150}
            allowDeselect={false}
            disabled={!entry.patientId || savingKeys.has(`${patientId}|status`)}
            onChange={(value) => {
              if (value) {
                handleSaveStatus(entry, value as RpmMonitoringStatus).catch(console.error);
              }
            }}
          />
        );
      },
    },
    {
      accessor: 'actions' as const,
      title: 'Actions',
      sortable: false,
      render: (entry: RemoteMonitoringEntry) => {
        const taskKey = `${entry.patientId ?? entry.patientName}|task`;
        const savingTask = savingKeys.has(taskKey);
        return (
          <Group gap="sm" wrap="nowrap">
            <TableAnchor size="sm" fw={700} c="#640040" onClick={() => setNoteEntry(entry)}>
              Add note
            </TableAnchor>
            <TableAnchor size="sm" onClick={() => setNotesEntry(entry)}>
              View Notes
            </TableAnchor>
            <TableAnchor
              size="sm"
              component="button"
              type="button"
              disabled={savingTask}
              onClick={() => {
                setTaskEntry(entry);
              }}
            >
              {savingTask ? 'Creating...' : 'Create task'}
            </TableAnchor>
            <TableAnchor size="sm" component="a" href={AIRVIEW_URL} target="_blank" rel="noreferrer">
              AirView
            </TableAnchor>
          </Group>
        );
      },
    },
  ];

  const sortStatus: DataTableSortStatus<RemoteMonitoringEntry> = (() => {
    const status = triageSortStateToSortStatus(sortState);
    if (!status) {
      return { columnAccessor: '', direction: 'desc' as const };
    }
    const columnAccessor = status.columnAccessor === 'name' ? 'patientName' : status.columnAccessor;
    return { columnAccessor, direction: status.direction };
  })();

  return (
    <>
      {total > 0 && (
        <Text size="sm" c="dimmed">
          {start} - {end} of {total}
        </Text>
      )}
      <DataTable
        striped
        highlightOnHover
        fetching={loading && entries.length === 0}
        records={entries}
        columns={columns}
        idAccessor="patientId"
        noRecordsText="No remote monitoring patients found"
        page={page}
        recordsPerPage={pageSize}
        totalRecords={total}
        onPageChange={onPageChange}
        paginationSize="sm"
        minHeight={200}
        sortStatus={sortStatus}
        onSortStatusChange={(status: DataTableSortStatus<RemoteMonitoringEntry>) => {
          const normalizedAccessor = status.columnAccessor === 'patientName' ? 'name' : status.columnAccessor;
          const next = sortStatusToTriageSortState({ columnAccessor: normalizedAccessor, direction: status.direction });
          if (
            next &&
            sortState &&
            sortState.column === next.column &&
            sortState.direction === 'asc' &&
            next.direction === 'desc'
          ) {
            onSortChange(null);
          } else {
            onSortChange(next);
          }
        }}
      />
      <RpmContactInfoModal
        entry={contactEntry}
        opened={Boolean(contactEntry)}
        onClose={() => setContactEntry(undefined)}
        onAddNote={(entry) => {
          setContactEntry(undefined);
          setNoteEntry(entry);
        }}
      />
      <RpmAddNoteModal
        entry={noteEntry}
        month={month}
        opened={Boolean(noteEntry)}
        onClose={() => setNoteEntry(undefined)}
        onSaved={refetch}
      />
      <RpmMonthNotesModal
        patientId={notesEntry?.patientId}
        patientName={notesEntry?.patientName}
        month={month}
        opened={Boolean(notesEntry)}
        onClose={() => setNotesEntry(undefined)}
      />
      <CreateTriageTaskModal
        opened={Boolean(taskEntry)}
        title="RPM follow-up"
        description="Review remote patient monitoring requirements and contact patient if needed."
        patientName={taskEntry?.patientName}
        defaultAssignee={profile as Practitioner | undefined}
        submitting={taskEntry ? savingKeys.has(`${taskEntry.patientId ?? taskEntry.patientName}|task`) : false}
        onClose={() => setTaskEntry(undefined)}
        onCreate={(owner, details) => {
          if (taskEntry) {
            handleCreateTask(taskEntry, owner, details).catch(console.error);
          }
        }}
      />
    </>
  );
}
